import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { API } from '@/api';
import type { CampaignTag } from '@/legacy-types/campaigns';
import { cloneDeep } from 'lodash';
import { utils } from '@/helpers';

interface CampaignTagsState {
	loaded: boolean;
	tags: Record<string, CampaignTag>;
	isLoading: boolean;
	error: string | null;
}

interface CampaignTagsActions {
	fetchTags: () => Promise<void>;
	updateCampaignTags: (campaignID: string, tags: CampaignTag[]) => Promise<void>;
	upsertCampaignTag: (tag: Partial<CampaignTag>) => Promise<CampaignTag | null>;
	deleteCampaignTag: (tagID: string) => Promise<void>;
}

export type CampaignTagsStore = CampaignTagsState & CampaignTagsActions;

const initialState: CampaignTagsState = {
	loaded: false,
	tags: {},
	isLoading: false,
	error: null,
};

export const useCampaignTagsStore = create<CampaignTagsStore>()(
	devtools(
		(set, get) => ({
			...initialState,

			fetchTags: async () => {
				try {
					set({ isLoading: true, error: null });
					const tags = await API.getAllCampaignTags();
					const tagsMap = tags.reduce(
						(acc, tag) => {
							acc[tag.id] = tag;
							return acc;
						},
						{} as Record<string, CampaignTag>,
					);
					set({ tags: tagsMap, loaded: true, isLoading: false });
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to fetch campaign tags',
						isLoading: false,
					});
				}
			},

			updateCampaignTags: async (campaignID, tags) => {
				try {
					set({ isLoading: true, error: null });
					const updatedTags = await API.updateCampaigTags(campaignID, tags);
					const existingTags = cloneDeep(get().tags);
					for (const tag of updatedTags) {
						existingTags[tag.id] = tag;
					}
					set({ tags: existingTags, isLoading: false });
					utils?.toaster?.addToast?.({
						message: 'Campaign tags updated',
						type: 'success',
					});
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to update campaign tags',
						isLoading: false,
					});
				}
			},

			upsertCampaignTag: async (tag) => {
				try {
					set({ isLoading: true, error: null });
					const updatedTag = await API.upsertCampaignTag(tag as CampaignTag);
					set({ tags: { ...get().tags, [updatedTag.id]: updatedTag }, isLoading: false });
					utils?.toaster?.addToast?.({
						message: 'Campaign tag created',
						type: 'success',
					});
					return updatedTag;
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to upsert campaign tag',
						isLoading: false,
					});
					return null;
				}
			},

			deleteCampaignTag: async (tagID) => {
				try {
					set({ isLoading: true, error: null });
					await API.deleteCampaignTag(tagID);
					set({ isLoading: false });
					utils?.toaster?.addToast?.({
						message: 'Campaign tag deleted',
						type: 'success',
					});
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to delete campaign tag',
						isLoading: false,
					});
				}
			},
		}),
		{
			name: 'campaign-tags-store',
		},
	),
);
